// One player interface over Shaka 2.5.23.
//
// The whole fleet runs 2.5.23 (see LoadShaka), whose API is not the one the app
// speaks: the player constructs bare and attaches the video separately rather
// than taking it in the constructor, it loads a URL rather than a
// DashVideoSource, and tracks are single "variants" selected by language rather
// than separate audio and video lists selected by id.
//
// Rather than translate at every call site across Player and PlayerControls, it
// is wrapped once here to look like the shape the app already speaks. That shape
// is Shaka 1.x's, which the app was written against and the Cobalt 11 boxes ran
// until they were retired (CP-2353).
import { getShaka, } from "./LoadShaka";
import { appHeaders } from "../services/api";
// Cobalt has no video.textTracks, and every text displayer Shaka 2 ships reads
// it while constructing — which rejects load() before a single byte of media
// moves. This one does nothing, which is no loss today: the caption list in
// PlayerControls is commented out, so nothing can turn subtitles on. Rendering
// them on Cobalt needs a displayer that paints into a div (CP-2018).
var NoopTextDisplayer = /** @class */ (function () {
    function NoopTextDisplayer() {
    }
    NoopTextDisplayer.prototype.configure = function () { };
    NoopTextDisplayer.prototype.append = function () { };
    NoopTextDisplayer.prototype.remove = function () {
        return true;
    };
    NoopTextDisplayer.prototype.destroy = function () {
        return Promise.resolve();
    };
    NoopTextDisplayer.prototype.isTextVisible = function () {
        return false;
    };
    NoopTextDisplayer.prototype.setTextVisibility = function () { };
    return NoopTextDisplayer;
}());
// Shaka 2 reports a cancelled load as an error. Tearing the player down while a
// load is still running is normal here — every episode and quality change does
// it — so these two codes are noise rather than failures.
var LOAD_INTERRUPTED = 7000;
var OPERATION_ABORTED = 7001;
var isAbortError = function (error) {
    var _a;
    var code = (_a = error) === null || _a === void 0 ? void 0 : _a.code;
    return code === OPERATION_ABORTED || code === LOAD_INTERRUPTED;
};
// Shaka 2 finishes tearing a player down by taking the video element away from
// it — removeAttribute("src") then load(), inside a promise chain that settles
// long after destroy() has returned. A quality change reuses the same <video>:
// the effect re-runs, the old player is dropped and a new one attaches, and the
// old player's teardown would then strip the src the new one just set, leaving a
// black frame and a spinner that never clears. Teardowns are therefore queued
// here, and a new player waits for the outstanding ones before it attaches.
var pendingTeardown = Promise.resolve();
// Shaka's teardown waits on the in-flight SourceBuffer operations, which only
// settle from updateend/error on the SourceBuffer itself. On a wedged pipeline
// those events may never arrive, and an unbounded wait would mean every later
// title hangs on a spinner for the rest of the session. Giving up on a teardown
// risks the src-stripping race this queue exists to prevent, but a bounded race
// beats a permanent hang.
var TEARDOWN_TIMEOUT_MS = 5000;
var queueTeardown = function (step) {
    pendingTeardown = pendingTeardown
        .then(function () {
        return new Promise(function (resolve) {
            var timer = setTimeout(function () {
                console.warn("[player] Shaka teardown timed out — continuing");
                resolve();
            }, TEARDOWN_TIMEOUT_MS);
            step().then(function () {
                clearTimeout(timer);
                resolve();
            }, function () {
                clearTimeout(timer);
                resolve();
            });
        });
    })
        .catch(function () { });
};
// Which language a 2.x track id belongs to. Both variant and text tracks carry
// the same two fields this needs.
var languageOf = function (tracks, id) {
    var numericId = Number(id);
    var track = tracks.find(function (candidate) { return candidate.id === numericId; });
    if (!track) {
        return "";
    }
    return track.language || "";
};
var OWN_API_PATH = "/api/v1/";
// The headers carry the STB dev key, so they go only to this app's own API,
// never to a CDN or licence server a manifest might point at.
var isOwnApiRequest = function (uris) {
    var ownApiUrl = window.location.protocol + "//" + window.location.host + OWN_API_PATH;
    return uris.every(function (uri) { return uri.indexOf(OWN_API_PATH) === 0 || uri.indexOf(ownApiUrl) === 0; });
};
var createV2Facade = function (player, video) {
    player.configure({
        abr: { enabled: true },
        streaming: {
            bufferingGoal: 15,
            bufferBehind: 10,
            jumpLargeGaps: true,
        },
        textDisplayFactory: NoopTextDisplayer,
    });
    // Shaka does its own networking, so without this its requests skip the headers
    // the axios client adds — and a local build off the Silk host then gets its
    // DRM-free streams refused.
    player.getNetworkingEngine().registerRequestFilter(function (_type, request) {
        if (isOwnApiRequest(request.uris)) {
            Object.assign(request.headers, appHeaders());
        }
    });
    // Whatever teardown was still outstanding when this player was built. The
    // previous player may still be holding the same <video>.
    var previousTeardown = pendingTeardown;
    // 2.x wants the video element attached separately, and attach() is async.
    // Doing it inside load() keeps construction synchronous, so the caller can
    // register listeners and tear down exactly as it does on 1.x.
    var attached;
    var attach = function () {
        if (!attached) {
            attached = previousTeardown.then(function () { return player.attach(video); });
        }
        return attached;
    };
    var variantsByLanguage = function () {
        var seen = new Set();
        var result = [];
        player.getVariantTracks().forEach(function (track) {
            var language = track.language || "";
            if (seen.has(language)) {
                return;
            }
            seen.add(language);
            result.push(track);
        });
        return result;
    };
    return {
        load: function (url) {
            return attach()
                .then(function () {
                // Silk stream URLs end in "/" with no extension, so Shaka has nothing
                // to guess a manifest parser from unless the type is named outright.
                return player.load(url, null, "application/dash+xml");
            })
                .catch(function (error) {
                if (isAbortError(error)) {
                    return;
                }
                throw error;
            });
        },
        // Fire-and-forget by design: ordering is handled by the queue, so no caller
        // has to await anything.
        //
        // destroy() already walks the full unload route on its way to detached, and
        // that route contains a hard 100ms timer, so unloading first would pay for
        // it twice on every quality change. The only caller does both back to back.
        unload: function () { },
        destroy: function () {
            queueTeardown(function () { return player.destroy(); });
        },
        // Nothing reads videoTracks — resolution switching swaps the source URL via
        // `mappers` rather than picking a rendition — so one entry per distinct
        // height is enough to keep the shape honest.
        getVideoTracks: function () {
            var seen = new Set();
            var result = [];
            player.getVariantTracks().forEach(function (track) {
                var height = track.height || 0;
                if (seen.has(height)) {
                    return;
                }
                seen.add(height);
                result.push({ id: track.id, lang: track.language || "" });
            });
            return result;
        },
        getAudioTracks: function () {
            return variantsByLanguage().map(function (track) { return ({
                id: track.id,
                lang: track.language || "",
            }); });
        },
        getTextTracks: function () {
            return player.getTextTracks().map(function (track) { return ({
                id: track.id,
                lang: track.language || "",
            }); });
        },
        // The app talks in track ids because that is what 1.x used, so the id has to
        // be resolved back to a language. Read it from the live track list rather
        // than a map the getters fill: after a quality or episode change the new
        // player's map would still be empty while the controls hold the previous
        // player's ids, and selecting would silently do nothing.
        //
        // Selecting the variant outright would pin the video rendition too and stop
        // ABR from adapting, so switch by language and let ABR keep its job.
        selectAudioTrack: function (id) {
            var language = languageOf(player.getVariantTracks(), id);
            if (language) {
                player.selectAudioLanguage(language);
            }
        },
        enableTextTrack: function (id) {
            var language = languageOf(player.getTextTracks(), id);
            if (language) {
                player.selectTextLanguage(language);
                // Async in 2.5.23, and it can reject — an unhandled rejection here
                // would be a crash rather than a subtitle that failed to appear.
                player.setTextTrackVisibility(true).catch(function (error) {
                    console.error("[player] enabling subtitles failed", error);
                });
            }
        },
        addEventListener: function (type, handler) {
            player.addEventListener(mapEventName(type), handler);
        },
        removeEventListener: function (type, handler) {
            player.removeEventListener(mapEventName(type), handler);
        },
    };
};
// The app listens for "adaptation" to mean "the track lists may have changed".
// In 2.x that news arrives as "trackschanged", which is also the event that
// fires once a manifest has been parsed.
var mapEventName = function (type) {
    if (type === "adaptation") {
        return "trackschanged";
    }
    return type;
};
/**
 * Builds a player. Synchronous, so it drops into the existing effect unchanged —
 * the asynchronous attach is folded into the first load() instead.
 */
export var createShakaPlayer = function (video) {
    var shaka = getShaka();
    if (!shaka.Player.isBrowserSupported()) {
        throw new Error("Shaka reports this browser is unsupported");
    }
    return createV2Facade(new shaka.Player(), video);
};



//////////////////
// WEBPACK FOOTER
// ./src/utils/ShakaPlayer.ts
// module id = 190
// module chunks = 1